import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Share2,
  Video,
  Images,
  MapPin,
  MoveDiagonal,
  BedSingle,
  Car,
  Bath,
  ChevronDown,
  Snowflake,
  Utensils,
  Shirt,
  WavesLadder,
  PawPrint,
  Microwave,
  FlameKindling,
  Sofa,
  Dumbbell,
  MoveVertical,
  ChefHat,
  Beef,
  CircleDot,
  Trophy,
  LayoutGrid,
  Accessibility,
  WashingMachine,
  House,
  PanelTopOpen,
  PartyPopper,
  Gamepad2,
  Leaf,
  ToyBrick,
  Film,
  Bike,
  UsersRound,
  CloudDrizzle,
  Flower,
  ShieldUser,
  Flame,
} from "lucide-react";
import FloatingChat from '@/components/layout/FloatingChat';
import { Header, Footer, MobileHeader } from "@/components/layout";
import MapEmbed from '@/components/common/MapEmbed';
import RecommendedProperties from '@/components/property/RecommendedProperties';
import GalleryModal, { type GalleryItem } from '@/components/common/GalleryModal';
import { PropertySEO } from '@/components/seo';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useIsMobile } from '@/hooks/use-mobile';
import { useImobiliaria } from '@/hooks/useImobiliaria';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useImovel, useImoveis } from '@/hooks/useImoveis';
import { useCreateLeadInteresse } from '@/hooks/useLeads';
import { useToast } from '@/hooks/use-toast';
import { imoveisToPropertyCards, imovelToPropertyCard } from '@/adapters/propertyAdapter';
import { MOCK_FEATURED_PROPERTIES } from '@/data/mockData';
import { cn } from '@/lib/utils';
import { normalizeYouTubeUrlOrEmbed } from '@/utils/videoHelpers';
import { getFotosUrls } from '@/utils/fotoHelpers';

export default function PropertyDetailPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [isDescriptionOpen, setIsDescriptionOpen] = useState(true);
  const [isPropertyFeaturesOpen, setIsPropertyFeaturesOpen] = useState(true);
  const [isCondominiumFeaturesOpen, setIsCondominiumFeaturesOpen] = useState(true);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [honeypot, setHoneypot] = useState("");
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState<'venda' | 'locacao' | null>(null);

  // Buscar dados da imobiliária
  const { data: imobiliaria } = useImobiliaria('desordibroker');
  const { data: siteConfig } = useSiteConfig(imobiliaria?.id);

  // Buscar imóvel específico
  const { data: imovel, isLoading: loadingImovel } = useImovel(codigo);

  // Debug: log da mídia do imóvel
  useEffect(() => {
    if (!imovel) return;
    console.log('🧩 PropertyDetail - mídia do imóvel', {
      codigo: imovel.codigo,
      videos: imovel.videos,
      tour_virtual: imovel.tour_virtual,
    });
  }, [imovel]);

  // Hooks para leads
  const createLeadInteresse = useCreateLeadInteresse();
  const { toast } = useToast();

  // Converter dados do imóvel
  const propertyCard = imovel ? imovelToPropertyCard(imovel) : null;
  const isRental = imovel?.finalidade.includes('locacao');
  const isSale = imovel?.finalidade.includes('venda');

  // Estado para rastrear qual nível de busca foi usado
  const [searchLevel, setSearchLevel] = useState<1 | 2 | 3 | 4>(1);

  // Construir filtros baseado no nível de busca
  const similarFilters = useMemo(() => {
    if (!imovel) return undefined;
    
    const baseFilters = {
      finalidade: (isRental ? 'locacao' : 'venda') as 'locacao' | 'venda',
    };
    
    switch (searchLevel) {
      case 1: // Busca rigorosa
        return {
          ...baseFilters,
          tipo: [imovel.tipo],
          quartos_min: imovel.quartos ? Math.max(1, imovel.quartos - 1) : undefined,
          valor_max: isRental 
            ? (imovel.valor_locacao || 0) * 2
            : (imovel.valor_venda || 0) * 2,
          bairros: imovel.endereco_bairro ? [imovel.endereco_bairro] : undefined,
        };
        
      case 2: // Busca média (sem bairro)
        return {
          ...baseFilters,
          tipo: [imovel.tipo],
          quartos_min: imovel.quartos ? Math.max(1, imovel.quartos - 2) : undefined,
          valor_max: isRental 
            ? (imovel.valor_locacao || 0) * 2.5
            : (imovel.valor_venda || 0) * 2.5,
        };
        
      case 3: // Busca ampla (apenas tipo e finalidade)
        return {
          ...baseFilters,
          tipo: [imovel.tipo],
        };
        
      case 4: // Busca genérica (apenas finalidade)
        return baseFilters;
        
      default:
        return baseFilters;
    }
  }, [imovel, isRental, searchLevel]);

  // Buscar imóveis similares
  const { data: imoveisSimilares } = useImoveis({
    imobiliariaId: imobiliaria?.id,
    limit: 3,
    exclude: imovel?.id ? [imovel.id] : [],
    filters: similarFilters,
    orderBy: { column: 'created_at', ascending: false },
  });

  // Efeito para escalar para nível superior se não encontrar resultados suficientes
  useEffect(() => {
    if (imoveisSimilares) {
      const count = imoveisSimilares.length;
      
      // Se encontrou menos de 3 e não está no último nível, escalar
      if (count < 3 && searchLevel < 4) {
        console.log(`🔍 Nível ${searchLevel} encontrou apenas ${count} imóveis. Escalando para nível ${searchLevel + 1}...`);
        setSearchLevel(prev => (prev + 1) as 1 | 2 | 3 | 4);
      } else if (count >= 3) {
        console.log(`✅ Nível ${searchLevel} encontrou ${count} imóveis sugeridos!`);
      }
    }
  }, [imoveisSimilares, searchLevel]);

  // Reset search level quando mudar de imóvel
  useEffect(() => {
    setSearchLevel(1);
  }, [codigo]);

  // Note: View tracking removed from client-side for security
  // Server-side analytics should handle view tracking

  // Construir endereço completo
  const enderecoCompleto = useMemo(() => {
    if (!imovel) return null;
    
    const partes = [
      imovel.endereco_logradouro,
      imovel.endereco_numero && `nº ${imovel.endereco_numero}`,
      imovel.endereco_complemento,
      imovel.endereco_bairro,
      imovel.endereco_cidade,
      imovel.endereco_uf
    ].filter(Boolean);
    
    return partes.length > 0 ? partes.join(', ') : null;
  }, [imovel]);

  // Converter imóveis similares
  const similarProperties = useMemo(() => {
    if (imoveisSimilares && imoveisSimilares.length > 0) {
      return imoveisToPropertyCards(imoveisSimilares);
    }
    return [];
  }, [imoveisSimilares]);

  // Preparar galeria de fotos
  const galleryItems: GalleryItem[] = useMemo(() => {
    if (!imovel?.fotos || imovel.fotos.length === 0) {
      return [
        {
          id: '1',
          type: 'image',
          src: '/img-default.jpg',
          alt: propertyCard?.title || 'Imóvel'
        }
      ];
    }

    // Usar helper para extrair URLs respeitando marca d'água
    const fotosUrls = getFotosUrls(imovel.fotos, imovel.marca_dagua || false);
    
    const items: GalleryItem[] = fotosUrls.map((url, index) => ({
      id: `foto-${index}`,
      type: 'image',
      src: url,
      alt: `${propertyCard?.title} - Foto ${index + 1}`
    }));

    // Adicionar vídeos do array 'videos' (HouseBit) - suporta strings ou objetos
    if (imovel.videos && Array.isArray(imovel.videos) && imovel.videos.length > 0) {
      imovel.videos.forEach((video: any, index: number) => {
        let videoSource: string | null = null;
        let videoTitle = `Vídeo ${index + 1}`;

        if (typeof video === 'string') {
          // Caso mais comum: array de strings com URL ou embed
          videoSource = video.trim();
          if (!videoSource) return;
        } else if (video && typeof video === 'object') {
          // Caso objeto: pode ter vários campos possíveis
          const stringValues = Object.values(video).filter(
            (v): v is string => typeof v === 'string' && v.trim().length > 0
          );

          // Procurar por iframe embed ou URL do YouTube
          const iframeValue = stringValues.find((v) => v.includes('<iframe'));
          const youtubeOrUrl = stringValues.find((v) =>
            /youtube\.com|youtu\.be|http(s)?:\/\//i.test(v)
          );

          videoSource = (iframeValue ?? youtubeOrUrl ?? '').trim();
          videoTitle = video.titulo || video.title || videoTitle;
        }

        if (!videoSource) {
          console.warn('⚠️ Video sem fonte válida', video);
          return;
        }

        // Normalizar a URL (se vier no formato embed, extrai o src)
        const finalUrl = normalizeYouTubeUrlOrEmbed(videoSource);
        if (!finalUrl) {
          console.warn('⚠️ Video não pôde ser normalizado', videoSource);
          return;
        }

        // Passar apenas a URL/embed HTML - a validação será feita no GalleryModal
        items.push({
          id: `video-${index}`,
          type: 'video',
          src: finalUrl,
          embedHtml: videoSource.includes('<iframe') ? videoSource : undefined,
          alt: videoTitle,
          thumbnail: items[0]?.src || '/img-default.jpg',
        });
      });
    }

    // Adicionar tour virtual se existir (compatibilidade)
    if (imovel.tour_virtual) {
      const rawValue = imovel.tour_virtual.toString();
      const normalized = normalizeYouTubeUrlOrEmbed(rawValue);
      if (normalized) {
        const hasIframe = rawValue.includes('<iframe');

        items.push({
          id: 'tour-virtual',
          type: 'video',
          src: normalized,
          alt: 'Tour virtual do imóvel',
          thumbnail: items[0]?.src || '/img-default.jpg',
          embedHtml: hasIframe ? rawValue : undefined,
        });
      }
    }

    console.log('🖼️ PropertyDetail - galleryItems', {
      codigo: imovel?.codigo,
      items,
    });

    return items;
  }, [imovel, propertyCard]);

  // Preparar features do imóvel
  const propertyFeatures = useMemo(() => {
    const features = [];
    const iconMap: Record<string, any> = {
      'ar-condicionado': Snowflake,
      'aceita animais': PawPrint,
      'varanda gourmet': Utensils,
      'cozinha americana': Microwave,
      'closet': Shirt,
      'lareira': FlameKindling,
      'piscina': WavesLadder,
      'mobiliado': Sofa,
    };

    if (imovel?.diferenciais) {
      imovel.diferenciais.forEach(diferencial => {
        const lowerDiferencial = diferencial.toLowerCase();
        const icon = Object.keys(iconMap).find(key => 
          lowerDiferencial.includes(key)
        );
        
        features.push({
          icon: icon ? iconMap[icon] : CircleDot,
          label: diferencial
        });
      });
    }

    return features;
  }, [imovel]);

  // Preparar features do condomínio
  const condominiumFeatures = useMemo(() => {
    const features = [];
    const iconMap: Record<string, any> = {
      'academia': Dumbbell,
      'churrasqueira': Beef,
      'elevador': MoveVertical,
      'salão de jogos': Gamepad2,
      'espaço gourmet': ChefHat,
      'jardim': Leaf,
      'quadra de squash': CircleDot,
      'cinema': Film,
      'piscina': WavesLadder,
      'playground': ToyBrick,
      'quadra de tênis': Trophy,
      'bicicletário': Bike,
      'quadra poliesportiva': LayoutGrid,
      'coworking': UsersRound,
      'acesso para pcd': Accessibility,
      'sauna': CloudDrizzle,
      'lavanderia': WashingMachine,
      'spa': Flower,
      'condomínio fechado': House,
      'portaria 24h': ShieldUser,
      'salão de festas': PartyPopper,
      'portão eletrônico': PanelTopOpen,
    };

    if (imovel?.diferenciais_condominio) {
      imovel.diferenciais_condominio.forEach(diferencial => {
        const lowerDiferencial = diferencial.toLowerCase();
        const icon = Object.keys(iconMap).find(key => 
          lowerDiferencial.includes(key)
        );
        
        features.push({
          icon: icon ? iconMap[icon] : CircleDot,
          label: diferencial
        });
      });
    }

    return features;
  }, [imovel]);

  // Handlers
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenGallery = (index: number = 0) => {
    setGalleryInitialIndex(index);
    setIsGalleryOpen(true);
  };

  const handleCloseGallery = () => {
    setIsGalleryOpen(false);
  };

  const handleShareMedia = async (item: GalleryItem) => {
    const shareData = {
      title: propertyCard?.title || 'Imóvel',
      text: `Confira este imóvel: ${propertyCard?.title}`,
      url: window.location.href
    };

    try {
      // Tentar usar API nativa de compartilhamento
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: copiar para clipboard
        await navigator.clipboard.writeText(window.location.href);
        
        // Mostrar feedback visual
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 3000);
      }
    } catch (error) {
      // Se usuário cancelou o compartilhamento, não fazer nada
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Erro ao compartilhar:', error);
        
        // Tentar copiar mesmo assim
        try {
          await navigator.clipboard.writeText(window.location.href);
          setShowCopyToast(true);
          setTimeout(() => setShowCopyToast(false), 3000);
        } catch (clipboardError) {
          alert('Não foi possível compartilhar. URL: ' + window.location.href);
        }
      }
    }
  };

  const handleNavigateToProperties = () => {
    navigate('/search-grid');
  };

  const handleNavigateToAbout = () => {
    navigate('/#about-section');
  };

  const handleAnnounceClick = () => {
    navigate('/anuncie');
  };

  const handleContactClick = () => {
    const whatsapp = siteConfig?.telefone_contato || '19982589820';
    const message = encodeURIComponent(`Olá! Tenho interesse no imóvel: ${propertyCard?.title || ''}`);
    window.open(`https://wa.me/55${whatsapp}?text=${message}`, '_blank');
  };

  const handleInterestClick = async () => {
    // Validação: nome e telefone obrigatórios
    if (!formData.name.trim()) {
      alert('Por favor, preencha seu nome');
      return;
    }
    if (!formData.phone.trim()) {
      alert('Por favor, preencha seu telefone');
      return;
    }

    // Nova validação: se tem ambas as finalidades, precisa escolher
    if (isSale && isRental && !selectedPurpose) {
      alert('Por favor, selecione se deseja comprar ou alugar');
      return;
    }

    // Validar se imobiliária está carregada
    if (!imobiliaria?.id) {
      alert('Erro ao carregar informações da imobiliária. Por favor, recarregue a página.');
      return;
    }

    // Validar se imóvel está carregado
    if (!imovel?.id) {
      alert('Erro ao carregar informações do imóvel. Por favor, recarregue a página.');
      return;
    }

    try {
      // Determinar tipo de negociação
      const tipoNegociacao = isSale && isRental 
        ? selectedPurpose! 
        : (isRental ? 'locacao' : 'venda');
      
      const valorPrincipal = tipoNegociacao === 'locacao'
        ? (imovel.valor_locacao || 0)
        : (imovel.valor_venda || 0);

      const leadData = {
        nome: formData.name.trim(),
        email: formData.email.trim() || undefined,
        telefone: formData.phone.trim() || undefined,
        imovelId: imovel.id,
        imobiliariaId: imobiliaria.id,
        tipoNegociacao: tipoNegociacao as 'locacao' | 'venda',
        valorPrincipal,
        _hp_field: honeypot, // Honeypot field for bot detection
      };

      console.log('📤 Enviando lead via Edge Function:', leadData);
      
      // Criar lead via Edge Function
      await createLeadInteresse.mutateAsync(leadData);

      // Abrir WhatsApp
      const whatsapp = siteConfig?.telefone_contato || '19982589820';
      const tipoInteresse = tipoNegociacao === 'locacao' ? 'alugar' : 'comprar';
      const message = encodeURIComponent(
        `Olá! Tenho interesse em ${tipoInteresse} o imóvel:\n\n` +
        `${propertyCard?.title || ''}\n` +
        `Código: ${imovel.codigo || ''}\n\n` +
        `Nome: ${formData.name}\n` +
        `Telefone: ${formData.phone}\n` +
        (formData.email ? `E-mail: ${formData.email}` : '')
      );
      window.open(`https://wa.me/55${whatsapp}?text=${message}`, '_blank');

      toast({
        title: "Sucesso!",
        description: "Suas informações foram enviadas. Você será redirecionado para o WhatsApp.",
      });

      // Limpar formulário e seleção
      setFormData({ name: '', phone: '', email: '' });
      setSelectedPurpose(null);
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      toast({
        title: "Erro",
        description: "Erro ao enviar informações. Por favor, tente novamente.",
        variant: "destructive",
      });
    }
  };

  // Loading state
  if (loadingImovel) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando imóvel...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!propertyCard) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold mb-2">Imóvel não encontrado</p>
          <p className="text-muted-foreground mb-4">O imóvel que você procura não está disponível</p>
          <button
            onClick={() => navigate('/search-grid')}
            className="px-6 py-2 bg-primary text-white rounded-full hover:bg-primary/90"
          >
            Ver todos os imóveis
          </button>
        </div>
      </div>
    );
  }

  // Calcular valores
  const mainPrice = isRental ? imovel?.valor_locacao : imovel?.valor_venda;
  const condominio = imovel?.valor_condominio || 0;
  const iptu = imovel?.valor_iptu || 0;
  // Para locação: aluguel + condomínio + IPTU
  // Para venda: apenas condomínio + IPTU (custos mensais)
  const totalPrice = isRental ? (mainPrice || 0) + condominio + iptu : condominio + iptu;

  // Formatar valores
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Preparar imagem principal
  const mainImage = galleryItems[0]?.src || '/img-default.jpg';
  const thumbnails = galleryItems.slice(1, 5);

  // Verificar se é novo (7 dias)
  const isNew = imovel?.created_at && 
    new Date(imovel.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      {imovel && (
        <ErrorBoundary fallback={null}>
          <PropertySEO 
            property={imovel}
            siteName={imobiliaria?.nome || 'Imobiliária'}
          />
        </ErrorBoundary>
      )}

      {/* Wrapper com largura máxima para monitores ultrawide */}
      <div
        className={`w-full mx-auto ${isMobile ? "max-w-[600px]" : "max-w-[1920px]"}`}
      >
        {isMobile ? (
          <MobileHeader
            onNavigateToProperties={handleNavigateToProperties}
            onNavigateToAbout={handleNavigateToAbout}
            onAnnounceClick={handleAnnounceClick}
          />
        ) : (
          <Header
            onNavigateToProperties={handleNavigateToProperties}
            onNavigateToAbout={handleNavigateToAbout}
            onAnnounceClick={handleAnnounceClick}
          />
        )}

        {/* Content */}
        <div className="flex flex-col items-center px-4 lg:px-10 gap-10">
          {/* Gallery Section */}
          <section
            className={cn(
              "w-full relative overflow-hidden flex-shrink-0",
              isMobile ? "h-[277px]" : "max-w-[1440px] h-auto lg:h-[524px]",
            )}
          >
            {isMobile ? (
              // Mobile Layout - Single image following Figma design
              <div className="relative w-full h-[277px]">
                <img
                  src={mainImage}
                  alt={propertyCard?.title || 'Imóvel'}
                  className="w-full h-full object-cover rounded cursor-pointer"
                  onClick={() => handleOpenGallery(0)}
                />

                {/* New Badge */}
                {isNew && (
                  <div className="absolute top-[20px] left-[16px] backdrop-blur-[5px] bg-white/50 flex items-center gap-2 px-4 py-1 rounded-[32px]">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-[16px] font-normal text-neutral-dark leading-[1.5]">
                      Anúncio novo
                    </span>
                  </div>
                )}

                {/* Share Button */}
                <button
                  className="absolute bottom-[16px] left-[16px] bg-white flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-50 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShareMedia(galleryItems[0]);
                  }}
                >
                  <Share2 className="w-6 h-6 text-neutral-black" />
                </button>

                {/* Gallery Buttons */}
                <div className="absolute bottom-[16px] right-[16px] flex items-center gap-4">
                  {galleryItems.some(item => item.type === 'video') && (
                    <button
                      className="bg-white flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-50 transition-colors"
                      onClick={() => handleOpenGallery(galleryItems.findIndex(item => item.type === 'video'))}
                    >
                      <Video className="w-6 h-6 text-neutral-black" />
                    </button>
                  )}
                  <button
                    className="bg-white flex items-center gap-2 px-8 py-2 rounded-full hover:bg-gray-50 transition-colors"
                    onClick={() => handleOpenGallery(0)}
                  >
                    <span className="text-[16px] font-normal text-neutral-black">
                      Ver mais fotos
                    </span>
                    <Images className="w-6 h-6 text-neutral-black" />
                  </button>
                </div>
              </div>
            ) : (
              // Desktop Layout
              <div className="flex flex-col lg:flex-row w-full h-full items-center gap-2">
                {/* Main Image */}
                <div
                  className="w-full lg:w-[695px] h-[300px] lg:h-[490px] relative cursor-pointer flex-shrink-0"
                  onClick={() => handleOpenGallery(0)}
                >
                  <img
                    src={mainImage}
                    alt={propertyCard?.title || 'Imóvel'}
                    className="w-full h-full max-h-[490px] object-cover rounded"
                  />

                  {/* New Badge */}
                  {isNew && (
                    <div className="absolute top-4 lg:top-9 left-4 lg:left-7 inline-flex items-center gap-2 px-4 py-1 rounded-full bg-white/50 backdrop-blur-sm">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="text-sm lg:text-base font-normal text-neutral-dark">
                        Anúncio novo
                      </span>
                    </div>
                  )}

                  {/* Share Button */}
                  <button
                    className="absolute bottom-4 lg:bottom-7 left-4 lg:left-7 flex items-center gap-2 px-6 lg:px-8 py-2 lg:py-3 rounded-full bg-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShareMedia(galleryItems[0]);
                    }}
                  >
                    <Share2 className="w-5 lg:w-6 h-5 lg:h-6 text-neutral-black" />
                    <span className="text-sm lg:text-base font-normal text-neutral-black">
                      Compartilhar
                    </span>
                  </button>
                </div>

                {/* Thumbnails */}
                <div className="w-full lg:w-[737px] h-[200px] lg:h-[490px] flex flex-row lg:flex-col gap-2 flex-shrink-0 overflow-hidden">
                  <div className="flex flex-row lg:flex-row items-center gap-2 flex-1">
                    {thumbnails.slice(0, 2).map((item, index) => (
                      <img
                        key={item.id}
                        src={item.src}
                        alt={item.alt}
                        className="flex-1 h-full object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleOpenGallery(index + 1)}
                      />
                    ))}
                  </div>
                  <div className="flex flex-row lg:flex-row items-center gap-2 flex-1">
                    {thumbnails.slice(2, 4).map((item, index) => (
                      <img
                        key={item.id}
                        src={item.src}
                        alt={item.alt}
                        className="flex-1 h-full object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleOpenGallery(index + 3)}
                      />
                    ))}
                  </div>
                </div>

                {/* Gallery Buttons */}
                <div className="absolute bottom-4 lg:bottom-[73px] right-4 lg:right-[32px] flex items-center gap-4">
                  {galleryItems.some(item => item.type === 'video') && (
                    <button
                      className="flex items-center gap-2 px-6 lg:px-8 py-2 lg:py-3 rounded-full bg-white hover:bg-gray-50 transition-colors"
                      onClick={() => handleOpenGallery(galleryItems.findIndex(item => item.type === 'video'))}
                    >
                      <span className="text-sm lg:text-base font-normal text-neutral-black">
                        Assistir o vídeo
                      </span>
                      <Video className="w-5 lg:w-6 h-5 lg:h-6 text-neutral-black" />
                    </button>
                  )}
                  <button
                    className="flex items-center gap-2 px-6 lg:px-8 py-2 lg:py-3 rounded-full bg-white hover:bg-gray-50 transition-colors"
                    onClick={() => handleOpenGallery(0)}
                  >
                    <span className="text-sm lg:text-base font-normal text-neutral-black">
                      Ver mais fotos
                    </span>
                    <Images className="w-5 lg:w-6 h-5 lg:h-6 text-neutral-black" />
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row justify-between items-start w-full max-w-[1440px] gap-10 relative z-10 mb-20">
            {/* Left Content */}
            <div className="flex flex-col items-start gap-10 w-full lg:w-[825px]">
              {/* Property Header */}
              <div className="flex flex-col justify-center items-start gap-6 w-full">
                <div className="flex flex-col justify-center items-start gap-4 w-full">
                  <div className="flex flex-col items-start gap-1">
                    {imovel?.codigo && (
                      <p className="text-sm font-medium text-gray-medium">
                        Código do imóvel:{" "}
                        <span className="font-normal">{imovel.codigo}</span>
                      </p>
                    )}
                    <h1 className="text-3xl lg:text-[40px] font-black text-neutral-black">
                      {propertyCard?.title || 'Imóvel'}
                    </h1>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-5 lg:w-6 h-5 lg:h-6 text-gray-medium" />
                      <span className="text-lg lg:text-2xl font-normal text-gray-medium">
                        {imovel?.endereco_cidade || 'Campinas'}, {imovel?.endereco_uf || 'SP'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Property Attributes */}
                <div className="flex flex-wrap justify-between items-center gap-4 lg:gap-8 w-full">
                  {imovel?.area && (
                    <div className="flex items-center gap-4">
                      <MoveDiagonal className="w-5 lg:w-6 h-5 lg:h-6 text-gray-medium" />
                      <span className="text-lg lg:text-2xl font-normal text-gray-medium">
                        {imovel.area}m²
                      </span>
                    </div>
                  )}
                  {imovel?.quartos && (
                    <div className="flex items-center gap-4">
                      <BedSingle className="w-5 lg:w-6 h-5 lg:h-6 text-gray-medium" />
                      <span className="text-lg lg:text-2xl font-normal text-gray-medium">
                        {imovel.quartos} {imovel.quartos === 1 ? 'quarto' : 'quartos'}
                      </span>
                    </div>
                  )}
                  {imovel?.vagas && (
                    <div className="flex items-center gap-4">
                      <Car className="w-5 lg:w-6 h-5 lg:h-6 text-gray-medium" />
                      <span className="text-lg lg:text-2xl font-normal text-gray-medium">
                        {imovel.vagas} {imovel.vagas === 1 ? 'vaga' : 'vagas'}
                      </span>
                    </div>
                  )}
                  {imovel?.banheiros && (
                    <div className="flex items-center gap-4">
                      <Bath className="w-5 lg:w-6 h-5 lg:h-6 text-gray-medium" />
                      <span className="text-lg lg:text-2xl font-normal text-gray-medium">
                        {imovel.banheiros} {imovel.banheiros === 1 ? 'banheiro' : 'banheiros'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description Section */}
              {imovel?.descricao && (
                <div className="flex flex-col items-center w-full">
                  <button
                    onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
                    className="flex justify-between items-center w-full py-4 border-b border-black/30"
                  >
                    <h2 className="text-xl lg:text-2xl font-bold text-neutral-black">
                      Descrição
                    </h2>
                    <ChevronDown
                      className={`w-6 h-6 text-gray-text transition-transform ${
                        isDescriptionOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isDescriptionOpen && (
                    <div className="w-full pt-6">
                      <p className="text-base font-normal text-gray-medium leading-relaxed whitespace-pre-wrap">
                        {imovel.descricao}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Property Features */}
              {propertyFeatures.length > 0 && (
                <div className="flex flex-col items-start gap-6 w-full py-4">
                  <button
                    onClick={() => setIsPropertyFeaturesOpen(!isPropertyFeaturesOpen)}
                    className="flex justify-between items-center w-full py-4"
                  >
                    <h2 className="text-xl lg:text-2xl font-bold text-neutral-black">
                      Diferenciais do Imóvel
                    </h2>
                    <ChevronDown
                      className={`w-6 h-6 text-gray-text transition-transform ${
                        isPropertyFeaturesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isPropertyFeaturesOpen && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-16 lg:gap-x-24 gap-y-6 w-full">
                      {propertyFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center gap-4">
                          <feature.icon className="w-5 lg:w-6 h-5 lg:h-6 text-gray-medium" />
                          <span className="text-base font-normal text-gray-medium">
                            {feature.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Condominium Features */}
              {condominiumFeatures.length > 0 && (
                <div className="flex flex-col items-start gap-6 w-full py-4">
                  <button
                    onClick={() => setIsCondominiumFeaturesOpen(!isCondominiumFeaturesOpen)}
                    className="flex justify-between items-center w-full py-4"
                  >
                    <h2 className="text-xl lg:text-2xl font-bold text-neutral-black">
                      Diferenciais do Condomínio
                    </h2>
                    <ChevronDown
                      className={`w-6 h-6 text-gray-text transition-transform ${
                        isCondominiumFeaturesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isCondominiumFeaturesOpen && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-16 lg:gap-x-24 gap-y-6 w-full">
                      {condominiumFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center gap-4">
                          <feature.icon className="w-5 lg:w-6 h-5 lg:h-6 text-gray-medium" />
                          <span className="text-base font-normal text-gray-medium">
                            {feature.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Map Section */}
              {enderecoCompleto && (
                <section className="flex flex-col items-start gap-4 w-full mt-6">
                  <h2 className="text-xl lg:text-2xl font-bold text-neutral-black py-4 border-t border-black/30 w-full">
                    Localização
                  </h2>
                  <div className="w-full flex justify-start mb-4">
                    <div className="inline-flex items-center gap-2">
                      <MapPin className="w-6 h-6 text-neutral-gray-dark" />
                      <p className="text-base font-normal text-neutral-gray-dark">
                        {enderecoCompleto}
                      </p>
                    </div>
                  </div>
                  <div className="w-full rounded-lg overflow-hidden">
                    <MapEmbed
                      address={enderecoCompleto}
                      height={400}
                    />
                  </div>
                </section>
              )}
            </div>

            {/* Right Sidebar - Contact Card */}
            <div className="flex flex-col items-start gap-6 w-full lg:w-[400px] p-6 rounded-3xl bg-white shadow-[0_4px_16px_0_rgba(0,0,0,0.16)] lg:sticky lg:top-4 order-last lg:order-none">
              {/* NOVA ESTRUTURA: Seção "Valores" */}
              <div className="flex flex-col items-start gap-6 w-full">
                <h3 className="text-xl lg:text-2xl font-bold text-neutral-black">
                  Valores
                </h3>
                
            {/* Para comprar (se disponível) */}
            {isSale && imovel?.valor_venda && (
              <div className="flex flex-col items-start gap-3 w-full pb-6 border-b border-black/30">
                <span className="text-sm font-medium text-neutral-gray-dark uppercase">
                  Para comprar
                </span>
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-start gap-1">
                    <span className="text-[10px] font-normal text-gray-medium mt-1">Venda</span>
                    <span className="text-3xl lg:text-4xl font-bold text-neutral-black">
                      {formatCurrency(imovel.valor_venda)}
                    </span>
                  </div>
                </div>
                
                {/* Custos adicionais para VENDA */}
                {(condominio > 0 || iptu > 0) && (
                  <div className="flex flex-col items-start gap-3 w-full mt-3 pt-3 border-t border-black/10">
                    <span className="text-xs font-medium text-gray-medium uppercase">
                      Custos mensais
                    </span>
                    {condominio > 0 && (
                      <div className="flex justify-between items-center w-full">
                        <span className="text-base font-light text-gray-medium">
                          Condomínio
                        </span>
                        <span className="text-sm font-normal text-neutral-dark">
                          {formatCurrency(condominio)}/mês
                        </span>
                      </div>
                    )}
                    {iptu > 0 && (
                      <div className="flex justify-between items-center w-full">
                        <span className="text-base font-light text-gray-medium">
                          IPTU
                        </span>
                        <span className="text-sm font-normal text-neutral-dark">
                          {formatCurrency(iptu)}/mês
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
                
                {/* Para alugar (se disponível) */}
                {isRental && imovel?.valor_locacao && (
                  <div className="flex flex-col items-start gap-3 w-full">
                    <span className="text-sm font-medium text-neutral-gray-dark uppercase">
                      Para alugar
                    </span>
                    
                    {/* Aluguel */}
                    <div className="flex flex-col items-start gap-1 w-full">
                      <div className="flex items-baseline gap-1">
                        <span className="text-[10px] font-normal text-gray-medium">Aluguel</span>
                        <span className="text-3xl lg:text-4xl font-bold text-neutral-black">
                          {formatCurrency(imovel.valor_locacao)}
                        </span>
                        <span className="text-xl lg:text-2xl font-normal text-neutral-black">
                          /mês
                        </span>
                      </div>
                    </div>
                    
                    {/* Custos adicionais */}
                    {(condominio > 0 || iptu > 0) && (
                      <div className="flex flex-col items-start gap-3 w-full">
                        {condominio > 0 && (
                          <div className="flex justify-between items-center w-full">
                            <span className="text-base font-light text-gray-medium">
                              Condomínio
                            </span>
                            <span className="text-sm font-normal text-neutral-dark">
                              {formatCurrency(condominio)}
                            </span>
                          </div>
                        )}
                        {iptu > 0 && (
                          <div className="flex justify-between items-center w-full">
                            <span className="text-base font-light text-gray-medium">
                              IPTU
                            </span>
                            <span className="text-sm font-normal text-neutral-dark">
                              {formatCurrency(iptu)}
                            </span>
                          </div>
                        )}
                        
                        {/* Valor total */}
                        <div className="flex justify-between items-center w-full pt-3 border-t border-black/10">
                          <span className="text-base font-medium text-neutral-black">
                            Valor total
                          </span>
                          <span className="text-base font-bold text-neutral-black">
                            {formatCurrency((imovel.valor_locacao || 0) + condominio + iptu)}/mês
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Separador */}
              <div className="w-full h-px bg-black/10"></div>
              
              {/* Contact Form */}
              <div className="flex flex-col items-start gap-6 w-full">
                {/* Honeypot field - hidden from users, catches bots */}
                <input
                  type="text"
                  name="_hp_field"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="absolute -left-[9999px] opacity-0"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                {/* Seletor de finalidade (quando tem ambas) */}
                {isSale && isRental && (
                  <div className="flex flex-col items-start gap-2 w-full">
                    <span className="text-sm font-normal text-neutral-black-alt">
                      Tenho interesse em: <span className="text-red-500">*</span>
                    </span>
                    <div className="flex gap-2 w-full">
                      <button
                        onClick={() => setSelectedPurpose('venda')}
                        className={cn(
                          "flex-1 py-2 px-4 rounded-full border transition-colors",
                          selectedPurpose === 'venda'
                            ? "bg-neutral-black text-white border-neutral-black"
                            : "bg-white text-neutral-black border-gray-300"
                        )}
                      >
                        Comprar
                      </button>
                      <button
                        onClick={() => setSelectedPurpose('locacao')}
                        className={cn(
                          "flex-1 py-2 px-4 rounded-full border transition-colors",
                          selectedPurpose === 'locacao'
                            ? "bg-neutral-black text-white border-neutral-black"
                            : "bg-white text-neutral-black border-gray-300"
                        )}
                      >
                        Alugar
                      </button>
                    </div>
                  </div>
                )}
                {/* Campo Nome (obrigatório) */}
                <div className="flex flex-col items-start gap-1 w-full">
                  <label className="text-sm font-normal text-neutral-black-alt">
                    Nome <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Maria da Silva"
                    required
                    className="w-full h-10 px-5 rounded-full border border-gray-300 bg-white text-base text-neutral-gray placeholder:text-neutral-gray"
                  />
                </div>

                {/* Campo Telefone (obrigatório) */}
                <div className="flex flex-col items-start gap-1 w-full">
                  <label className="text-sm font-normal text-neutral-black-alt">
                    Telefone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="(19) 99999-9999"
                    required
                    className="w-full h-10 px-5 rounded-full border border-gray-300 bg-white text-base text-neutral-gray placeholder:text-neutral-gray"
                  />
                </div>

                {/* Campo E-mail (opcional) */}
                <div className="flex flex-col items-start gap-1 w-full">
                  <label className="text-sm font-normal text-neutral-black-alt">
                    E-mail
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="email@dominio.com"
                    className="w-full h-10 px-5 rounded-full border border-gray-300 bg-white text-base text-neutral-gray placeholder:text-neutral-gray"
                  />
                </div>
              </div>

              {/* Interest Button */}
              <button 
                onClick={handleInterestClick}
                disabled={createLeadInteresse.isPending}
                className="flex justify-center items-center w-full py-4 rounded-full bg-neutral-black hover:bg-neutral-black/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white text-lg lg:text-[22px] font-normal"
              >
                {createLeadInteresse.isPending ? 'Enviando...' : 'Tenho interesse'}
              </button>

              {/* View Counter - Abaixo do botão */}
              {imovel?.metricas_visualizacoes != null && 
               Number(imovel.metricas_visualizacoes) > 0 && (
                <div className="flex items-center justify-center gap-2 w-full">
                  <Flame className="w-5 h-5 text-orange-500" />
                  <span className="text-base font-medium text-neutral-dark">
                    {imovel.metricas_visualizacoes} {Number(imovel.metricas_visualizacoes) === 1 ? 'visualização' : 'visualizações'}
                  </span>
                </div>
              )}
            </div>
          </div>


          {/* Suggested Properties - Apenas se houver similares */}
          {similarProperties.length > 0 && (
            <RecommendedProperties
              className="mt-10 w-full max-w-[1440px]"
              title="Imóveis Sugeridos"
              properties={similarProperties}
              onViewAll={() => navigate('/search-grid')}
            />
          )}
        </div>

        <Footer onContactClick={handleContactClick} />
      </div>

      <FloatingChat
        position="bottom-right"
        size="md"
        onClick={handleContactClick}
      />

      {/* Gallery Modal */}
      <GalleryModal
        isOpen={isGalleryOpen}
        onClose={handleCloseGallery}
        items={galleryItems}
        initialIndex={galleryInitialIndex}
        onShare={handleShareMedia}
      />

      {/* Toast de confirmação de cópia */}
      {showCopyToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-neutral-black text-white px-6 py-3 rounded-full shadow-lg animate-in fade-in slide-in-from-bottom-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Link copiado para a área de transferência!</span>
          </div>
        </div>
      )}
    </div>
  );
}
